// TODO: Fix this auto-generated suppression.
// eslint-disable-next-line rulesdir/require-next-link-wrapper
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import * as React from 'react'
import styled from 'styled-components'

import {
  Clock,
  ResponsiveImage,
  Size,
  Skeleton,
  TextLink,
  TypeStyleBodySmall,
  fromMd,
} from '@syconium/little-miss-figgy'

import { useTrackProductInteraction } from '../../../../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { MultiAddToBagService } from '../../../../../../brunswick/containers/multi-add-to-bag'
import {
  EmbroideryService,
  KitEmbroideryService,
} from '../../../../../../containers/EmbroideryService'
import { useUpdateQueryParam } from '../../../../../../lib/adapters/next-routing-service/useUpdateQueryParam'
import { trackEvent } from '../../../../../../lib/analytics'
import { useProductReviews } from '../../../../../../lib/hooks/useProductReviews'
import { useStackedColorsAndSizes } from '../../../../../../lib/hooks/useStackedColorsAndSizes'
import { useProductHref, useProductReviewsHref } from '../../../../../../lib/routing'
import { ProductColorWithUnavailable } from '../../../../../../types/figs'
import { EmbroideryVariant, ProductAsset, Variant } from '../../../../../../types/graphql'
import { ColorsSection } from '../../../ProductPage/DetailsSection/ColorsSection'
import { EmbroiderySection } from '../../../ProductPage/DetailsSection/EmbroiderySection'
import { FitsSection } from '../../../ProductPage/DetailsSection/FitsSection'
import { SizesSection } from '../../../ProductPage/DetailsSection/SizesSection'
import { SizeChart } from '../../../ProductPage/DetailsSection/SizesSection/SizeChart'
import { BadgesSection } from '../../../ProductPage/ProductHighlights/BadgesSection'

import SimpleReviewsSection from './SimpleReviewsSection'
import {
  BadgesWrapper,
  ColorsSectionWrapper,
  HighlightsContainer,
  ImagePlaceholder,
  Info,
  OptionsContainer,
  Thumbnail,
  Title,
  ViewDetailsButton,
} from './styles'

export interface KitDetailsSectionProps {
  activeProductId: string
  activeProductShopifyId: string
  colors: ProductColorWithUnavailable[]
  coloredAndFit: Variant[]
  embroiderable: boolean
  finalSale: boolean
  fitKeys: ReadonlyArray<string>
  fitNames?: ReadonlyMap<string, string>
  id: string
  isCurrentColorCompletelySoldOut: boolean
  isProductWaitlistable: boolean
  image: ProductAsset | undefined
  index: number
  keyColor: string
  keySize: string
  keyFit: string
  kitHandle: string
  openDetailsModal: () => void
  noProductDetailsModal: boolean
  productGroupHandle: string
  selectedColorKey: string | undefined
  selectedFitKey: string | undefined
  selectedSize: Size | undefined
  selectedVariant: Variant | null | undefined
  updateSelectedVariants: (variant: Variant, index: number) => void
  sizeChart?: SizeChart
  sizes: ReadonlyArray<Size>
  title: string
}

export const KitDetailsSection: React.FC<KitDetailsSectionProps> = ({
  activeProductId,
  activeProductShopifyId,
  colors,
  coloredAndFit,
  embroiderable,
  finalSale,
  fitKeys,
  fitNames,
  id,
  isCurrentColorCompletelySoldOut,
  isProductWaitlistable,
  image,
  index,
  keyColor,
  keySize,
  keyFit,
  kitHandle,
  openDetailsModal,
  noProductDetailsModal,
  productGroupHandle,
  selectedColorKey,
  selectedFitKey,
  selectedSize,
  selectedVariant,
  updateSelectedVariants,
  sizeChart,
  sizes,
  title,
}) => {
  const { useLargeWidthSizeSelectorItems } = useStackedColorsAndSizes(coloredAndFit?.[0]?.category)
  const { trackProductInteraction } = useTrackProductInteraction()

  const {
    pages: { product: productPageStrings },
  } = useTranslation()

  const multiAddToBagService = MultiAddToBagService.useContainer()
  const addToBagErrors = multiAddToBagService.addToBagErrors
  const doesSizeTitleShowError: boolean = !selectedSize && addToBagErrors.missingRequiredSize

  const updateQueryParam = useUpdateQueryParam()
  function makeUpdateQueryParam<S>(key: string, extractValue: (selection: S) => string) {
    return function (selection: S) {
      updateQueryParam(key, extractValue(selection))
    }
  }

  function sizeSelected<S>(key: string, extractValue: (selection: S) => string) {
    return function (selection: S) {
      const newSelectedVariant =
        coloredAndFit.length === 1
          ? coloredAndFit[0]
          : coloredAndFit.find(product => product.size === extractValue(selection))
      if (newSelectedVariant) updateSelectedVariants(newSelectedVariant, index)
      updateQueryParam(key, extractValue(selection))
    }
  }

  const embroideryService = EmbroideryService.useContainer()
  const haveEmbroideryOptionsBeenSelected: boolean = embroideryService.confirmAddEmbroidery

  const embroideryItemVariant =
    embroideryService.selectedEmbroideryFormOptions?.embroideryItemVariant
  const embroiderySetupVariant =
    embroideryService.selectedEmbroideryFormOptions?.embroiderySetupVariant
  const embroideryProperties = embroideryService.embroideryLineItemProperties
  const validEmbroidery =
    !!embroideryService.selectedEmbroideryFormOptions &&
    embroideryService.confirmAddEmbroidery &&
    embroideryItemVariant

  const kitEmbroideryService = KitEmbroideryService.useContainer()

  const onProductViewClick = () => {
    trackProductInteraction({
      handle: productGroupHandle,
      color: selectedColorKey ?? '',
      shopifyId: activeProductShopifyId,
      id: activeProductId,
    })
  }

  React.useEffect(() => {
    const isValid = !!validEmbroidery
    if (!isValid) {
      kitEmbroideryService.removeEmbroideryCartOption(id)
    } else {
      if (embroideryItemVariant && embroideryProperties) {
        kitEmbroideryService.updateEmbroideryCartOption({
          id: selectedVariant?.shopifyId || '',
          valid: isValid,
          embroideryItemVariant: embroideryItemVariant as EmbroideryVariant,
          embroiderySetupVariant: embroiderySetupVariant as EmbroideryVariant,
          embroideryLineItemProperties: embroideryProperties,
        })
      }
    }
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validEmbroidery, embroideryProperties, embroideryItemVariant, embroiderySetupVariant])

  const { productReviews, productReviewsStatus } = useProductReviews({
    shopifyProductId: activeProductShopifyId,
  })

  const {
    pages,
    cart: { finalSale: finalSaleLabel },
    detailsSection: {
      detailsModal: { viewDetails },
    },
  } = useTranslation()

  // plain=true query param is used by iOS app to view webpages within a webview without the website's header/footer
  const plainQueryParamValue = useSearchParams()?.get('plain') ?? undefined

  const href = useProductHref(productGroupHandle, {
    color: selectedColorKey,
    plain: plainQueryParamValue,
  })
  const reviewsHref = useProductReviewsHref({
    handle: productGroupHandle,
    colorKey: selectedColorKey,
    query: {
      kit: kitHandle,
      plain: plainQueryParamValue,
    },
  })

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openDetailsModal()
    }
  }
  const ProductThumbnail = () => {
    return (
      <Thumbnail>
        {image?.source ? (
          <ResponsiveImage
            aspectRatios={{ sm: 1, md: 1 }}
            loading={'lazy'}
            widths={{
              unit: 'px',
              sm: 80,
              md: 80,
            }}
            alt={image?.caption ?? productPageStrings.imageThumbnailAltText}
            src={image.source}
            transparentBackground={true}
            {...trackEvent({
              category: 'kits',
              action: 'click product image thumbnail xxx',
              label: 'thumbnail #',
            })}
          />
        ) : (
          <ImagePlaceholder />
        )}
      </Thumbnail>
    )
  }

  const displayReviewsSkeleton =
    productReviewsStatus === 'idle' || productReviewsStatus === 'pending'
  return (
    <>
      <HighlightsContainer>
        {noProductDetailsModal ? (
          <ProductThumbnail />
        ) : (
          <Link href={href} onClick={onProductViewClick}>
            <ProductThumbnail />
          </Link>
        )}
        <Info>
          {noProductDetailsModal ? (
            <Title>{title}</Title>
          ) : (
            <TextLink
              as='a'
              variant={'underline-on-hover'}
              onClick={onProductViewClick}
              href={href}
              {...trackEvent({
                category: 'kit details',
                action: 'snapshot',
                label: 'title',
                value: title,
              })}
            >
              {title}
            </TextLink>
          )}
          {productReviews?.averageScore ? (
            <Link href={reviewsHref}>
              <SimpleReviewsSection productReviews={productReviews} />
            </Link>
          ) : (
            displayReviewsSkeleton && (
              <Skeleton height='20px' width='220px' animated={true}></Skeleton>
            )
          )}
          {(finalSale || haveEmbroideryOptionsBeenSelected) && (
            <BadgesWrapper>
              <BadgesSection badges={[finalSaleLabel]} />
            </BadgesWrapper>
          )}
          {!noProductDetailsModal && (
            <ViewDetailsButton
              onClick={openDetailsModal}
              role='button'
              onKeyDown={event => handleKeyDown(event)}
              tabIndex={0}
              {...trackEvent({
                category: 'kits',
                action: 'click open product details',
                label: href,
              })}
            >
              {viewDetails}
            </ViewDetailsButton>
          )}
        </Info>
      </HighlightsContainer>

      <OptionsContainer>
        <ColorsSectionWrapper>
          <ColorsSection
            colors={colors}
            isProductWaitlistable={isProductWaitlistable}
            selectedColorKey={selectedColorKey}
            hasSelectedSize={!!selectedSize}
            isCurrentColorCompletelySoldOut={isCurrentColorCompletelySoldOut}
            onColorSelect={makeUpdateQueryParam(keyColor, color => color.rawName)}
          />
        </ColorsSectionWrapper>
        {selectedVariant?.isLowInventory && !selectedVariant?.soldOut && (
          <LowInventoryTypography>
            <Clock />
            {pages.product.lowInventoryWarning}
          </LowInventoryTypography>
        )}
        {sizes.length > 0 && !(sizes.length === 1 && sizes[0] && sizes[0].shorthand === 'O/S') && (
          <SizesSection
            isProductWaitlistable={isProductWaitlistable}
            selectedSize={selectedSize}
            sizeChart={sizeChart}
            sizes={sizes}
            minWidthStyle={useLargeWidthSizeSelectorItems ? 'lg' : undefined}
            isInvalid={doesSizeTitleShowError}
            onSizeSelect={sizeSelected<Size>(keySize, size => size.shorthand)}
            disableMobileHorizontalSwipe
          />
        )}

        {fitKeys.length > 0 && (
          <FitsSection
            fitKeys={fitKeys}
            fitNames={fitNames}
            onFitSelect={makeUpdateQueryParam<string>(keyFit, fit => fit)}
            selectedFitKey={selectedFitKey}
            title={productPageStrings.scrubTopsFitTitle}
            disableMobileHorizontalSwipe
          />
        )}
        <EmbroiderySection embroiderable={embroiderable} />
      </OptionsContainer>
    </>
  )
}
const LowInventoryTypography = styled(TypeStyleBodySmall)`
  color: ${o => o.theme.color.text.caution.on.background};
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(2)};
  padding: ${o => o.theme.spacing(2, 4)};
  ${fromMd} {
    padding: ${o => o.theme.spacing(2, 0)};
  }
`
